import type { WorkoutPlanInfoId } from '@az3fr/core';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
	SaveStatus,
	ValidationError,
	WorkoutCreatorState,
} from '@/features/workout-creator/model/workoutCreator.types';

const initialState: WorkoutCreatorState = {
	activePlanId: null,
	isDirty: false,
	saveStatus: 'idle',
	lastSavedAt: null,
	validationErrors: [],
};

const workoutCreatorSlice = createSlice({
	name: 'features/workoutCreator',
	initialState,
	reducers: {
		// Plan management actions
		activePlanIdSet(state, action: PayloadAction<WorkoutPlanInfoId | null>) {
			state.activePlanId = action.payload;
		},

		// Dirty state management
		dirtyFlagSet(state, action: PayloadAction<boolean>) {
			state.isDirty = action.payload;
		},

		// Save status management
		saveStatusSet(state, action: PayloadAction<SaveStatus>) {
			state.saveStatus = action.payload;
			if (action.payload === 'saved') {
				state.lastSavedAt = Date.now();
				state.isDirty = false;
			}
		},

		// Validation error management
		validationErrorsSet(state, action: PayloadAction<ValidationError[]>) {
			state.validationErrors = action.payload;
		},

		validationErrorAdded(state, action: PayloadAction<ValidationError>) {
			state.validationErrors.push(action.payload);
		},

		validationErrorsCleared(state) {
			state.validationErrors = [];
		},

		validationErrorRemoved(
			state,
			action: PayloadAction<{
				entityType: string;
				entityId: string;
				field?: string;
			}>,
		) {
			const { entityType, entityId, field } = action.payload;
			state.validationErrors = state.validationErrors.filter((error) => {
				if (error.entityType !== entityType || error.entityId !== entityId) {
					return true;
				}
				if (field && error.field !== field) {
					return true;
				}
				return false;
			});
		},

		// Reset state (useful for cleanup)
		stateReset() {
			return initialState;
		},
	},
});

const workoutCreatorSliceActions = { ...workoutCreatorSlice.actions };
const workoutCreatorSliceReducer = workoutCreatorSlice.reducer;

export { workoutCreatorSliceActions, workoutCreatorSliceReducer };
export type { WorkoutCreatorState };
